import { useMutation } from '@apollo/client'
import { useToast } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { EXPORT_ISSUE, EXPORT_ISSUES_MASS } from '@/modules/exports/mutations'
import { useNavigate } from 'react-router'
import { useOnErrorAuto } from '@/utils/hooks'
import { CREATE_ISSUE_MUTATION, PATCH_ISSUE_MUTATION } from './mutations'
import type {
  CreateIssueMutation,
  CreateIssueMutationVariables,
  PatchIssueMutation,
  PatchIssueMutationVariables,
} from './types.graphl'

export function useCreateIssueMutation() {
  const translations = useTranslate({
    success: {
      create: 'issues.toasts.create-issue-success',
    },
    error: {
      create: 'issues.errors.create-issue',
    },
  })

  const navigate = useNavigate()

  const onErrorAuto = useOnErrorAuto()
  const addToast = useToast()

  const [createIssue, { loading: createLoading }] = useMutation<
    CreateIssueMutation,
    CreateIssueMutationVariables
  >(CREATE_ISSUE_MUTATION, {
    onCompleted(data) {
      if (!data) {
        addToast('error', translations.error.create)
        return
      }

      addToast('success', translations.success.create)
      void navigate(`/issues/${data.createIssue.issue.id}`)
    },
    onError: onErrorAuto(translations.error.create),
  })

  return {
    create: createIssue,
    loading: createLoading,
  }
}

export function usePatchIssueMutation() {
  const translations = useTranslate({
    success: {
      patch: 'issues.toasts.patch-issue-success',
    },
    error: {
      patch: 'issues.errors.patch-issue',
    },
  })

  const onErrorAuto = useOnErrorAuto()
  const addToast = useToast()

  const [patchIssue, { loading: patchLoading }] = useMutation<
    PatchIssueMutation,
    PatchIssueMutationVariables
  >(PATCH_ISSUE_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries: ['Issue'],
    onCompleted() {
      addToast('success', translations.success.patch)
    },
    onError: onErrorAuto(translations.error.patch),
  })

  return {
    patch: patchIssue,
    loading: patchLoading,
  }
}

export function useExportIssuesMutations(onCompleted?: () => void) {
  const translations = useTranslate({
    mailSuccess: 'issues.send-email-success',
    downloadSucces: 'exports.success-user-exports',
    exportTimeout: 'exports.export-timeout',
    error: 'exports.error-email-process',
  })

  const onErrorAuto = useOnErrorAuto()
  const addToast = useToast()

  const [exportIssue, { loading: mailLoading }] = useMutation(EXPORT_ISSUE, {
    onCompleted(data) {
      if (data?.exportIssue.exportTimeout) {
        addToast('info', translations.exportTimeout)
      } else if (data?.exportIssue.ok) {
        addToast('success', translations.downloadSucces)
      } else {
        onErrorAuto(translations.error)
      }
      onCompleted?.()
    },
  })

  const [exportIssuesMass, { loading: massMailLoading }] = useMutation(
    EXPORT_ISSUES_MASS,
    {
      onCompleted(data) {
        if (data?.exportIssuesMass.exportTimeout) {
          addToast('info', translations.exportTimeout)
        } else if (data?.exportIssuesMass.ok) {
          addToast('success', translations.downloadSucces)
        } else {
          onErrorAuto(translations.error)
        }
        onCompleted?.()
      },
    }
  )

  return {
    exportIssue,
    exportIssuesMass,
    loading: mailLoading || massMailLoading,
  }
}
